import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getSchema } from "./schemaStorage";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

// Lightweight request helper for imperative calls (POST/PUT/PATCH/DELETE or custom GETs)
// Returns the native Response to remain compatible with existing call sites
export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  // Do not pre-consume the body or log large payloads here to avoid main-thread overhead
  // Let callers decide how to handle non-OK responses
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
type QueryClientType = "public" | "dashboard";

/**
 * Helper to determine if we're in dashboard context
 */
function isDashboardContext(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.pathname.startsWith('/dashboard');
}

export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
  clientType?: QueryClientType;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior, clientType }) =>
  async ({ queryKey }) => {
    // Handle schema-scoped query keys: [schema, ...rest] or legacy [url, ...rest]
    // If first element is schema (matches current schema), URL is at index 1
    // Otherwise, URL is at index 0 (backward compatibility)
    const schema = getSchema();
    let url: string;
    if (queryKey[0] === schema && typeof queryKey[1] === 'string') {
      // Schema-scoped key: [schema, url, ...rest]
      url = queryKey[1] as string;
    } else {
      // Legacy key: [url, ...rest] or non-string first element
      url = queryKey[0] as string;
    }
    
    const headers: HeadersInit = {};
    
    // Add cache-busting headers for dashboard queries
    const isDashboard = clientType === 'dashboard' || isDashboardContext();
    if (isDashboard) {
      headers['Cache-Control'] = 'no-cache';
      headers['X-No-Cache'] = 'true';
    }
    
    const res = await fetch(url, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Public Query Client - Aggressive caching for public routes (SEO + high traffic)
export const publicQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw", clientType: "public" }),
      refetchInterval: false,
      refetchOnWindowFocus: false, // Don't refetch on focus for public (better UX)
      staleTime: 5 * 60 * 1000, // 5 minutes - aggressive cache for public
      gcTime: 30 * 60 * 1000, // 30 minutes - longer cache for public
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Dashboard Query Client - Fresh data for dashboard (admin needs real-time)
// No caching to ensure data consistency and prevent stale data issues
export const dashboardQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw", clientType: "dashboard" }),
      refetchInterval: false,
      refetchOnWindowFocus: true, // Refresh when returning to dashboard
      refetchOnMount: true, // Always refetch on mount for fresh data
      staleTime: 0, // Always consider data stale - fetch fresh data
      gcTime: 0, // Immediately remove from cache when unused (was 5 minutes)
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Default query client (backward compatibility - will be replaced by public/dashboard)
export const queryClient = publicQueryClient;
